/**
 * security-landing-renderer.ts
 *
 * High-Aesthetic Responsive HTML & CSS Renderer for Institutional Security Landing Page:
 * - Compiles full 7-section CmsPageTree into semantic, accessible, SEO-optimized HTML5.
 * - Enriches all content blocks with verified media imagery, responsive figures, and localized alt texts.
 * - Applies sleek dark-mode aesthetics with glassmorphism, accent badges, and responsive grids.
 * - Implements interactive tabs for service pillars, comparative matrix cards, and lead capture.
 */

import type {
  CmsPageTree,
  CmsHeroBannerBlock,
  CmsComparisonMatrixBlock,
  CmsServiceCatalogBlock,
  CmsTechEngineBlock,
  CmsIndustryMatrixBlock,
  CmsTrustAuditBlock,
  CmsLeadCaptureFormBlock,
  SupportedLocale,
  CmsBlockMediaAttachment,
} from '../cms-contracts.js';
import type { ThemeDefinition, StandaloneExportPolicy } from '../../../theme/theme-contracts.js';
import { DtcgCssCompiler } from '../../../theme/compiler/dtcg-css-compiler.js';
import {
  SOVEREIGN_DARK,
  SOVEREIGN_LIGHT,
  MIDNIGHT_NORDIC,
  INDUSTRIAL_AMBER,
  HIGH_CONTRAST,
  EMERALD_MATRIX,
  CYBERPUNK_NEON,
} from '../../../theme/presets/canonical-presets.js';
import {
  CANONICAL_I18N_DICTIONARIES,
} from '../../../i18n/semantic-message-dictionary.js';
import { createSecurityCompanyLandingPageTree } from '../fixtures/security-company-landing-page.js';
import { SemanticLandingTranslator } from './semantic-landing-translator.js';


export interface StandalonePageConfig {
  readonly title?: string;
  readonly defaultThemeId?: string;
  readonly defaultLocale?: string;
  readonly activeLocale?: SupportedLocale;
  readonly publishedLocales?: readonly SupportedLocale[];
  readonly baseDomain?: string;
  readonly heroMediaAttachment?: CmsBlockMediaAttachment;
  readonly socDiagramMediaAttachment?: CmsBlockMediaAttachment;
  readonly heroImageUrl?: string;
  readonly socDiagramUrl?: string;
  readonly brandName?: string;
  readonly pageTree?: CmsPageTree;
}

export interface LocalizedLandingConfig extends StandalonePageConfig {}

export class SecurityLandingRenderer {
  public static readonly METADATA_DICTIONARIES: Record<SupportedLocale, { title: string; desc: string; ogTitle: string; ogDesc: string; heroEyebrow: string; heroTitle: string; heroDesc: string }> = {
    'en-US': {
      title: 'Sovereign Asset Defense | Institutional Asset Protection & Vault Custody',
      desc: 'Bank-grade private vault custody, armored bullion logistics (CIT), and 24/7 AI-assisted SOC surveillance engineered for Uganda’s critical development sectors.',
      ogTitle: 'Sovereign Asset Defense Uganda',
      ogDesc: 'Protecting Critical Assets. Securing Uganda’s Economic Future.',
      heroEyebrow: 'Institutional Security & Asset Protection',
      heroTitle: 'Protecting What Matters. Securing Uganda’s Future.',
      heroDesc: 'From high-security private vaults and cash logistics to AI-assisted surveillance and industrial asset protection—engineered for Uganda\'s rapidly expanding economic frontiers.',
    },
    'sw-KE': {
      title: 'Ulinzi wa Rasilimali za Sovereign | Hifadhi ya Vyumba vya Siri na Usalama',
      desc: 'Vyumba vya siri vya benki, usafirishaji wa dhahabu na fedha (CIT), na ulinzi wa kamera za AI wa saa 24 nchini Uganda.',
      ogTitle: 'Ulinzi wa Rasilimali za Sovereign Uganda',
      ogDesc: 'Kulinda Rasilimali Muhimu. Kulinda Mustakabali wa Uganda.',
      heroEyebrow: 'Ulinzi wa Rasilimali za Kitaasisi',
      heroTitle: 'Kulinda Rasilimali Muhimu. Kulinda Mustakabali wa Uganda.',
      heroDesc: 'Kuanzia hifadhi ya vyumba vya siri vya benki hadi usafirishaji wa dhahabu (CIT) na ulinzi wa kamera za AI wa saa 24 nchini Uganda.',
    },
    'lg-UG': {
      title: 'Obukuumi bw’Eby’Obugagga bwa Sovereign | Amasanduuko g’Ebyama n’Obukuumi',
      desc: 'Amasanduuko g’ebyama ag’omulembe, emmotoka ezitambuza sente ne zaabu (CIT), n’obukuumi bwa kamera za AI obwa saawa 24 mu Uganda.',
      ogTitle: 'Obukuumi bw’Eby’Obugagga bwa Sovereign Uganda',
      ogDesc: 'Okukuuma Eby’Obugagga Ebikulu. Okutebenkeza Ebiseera by’e Ggwanga.',
      heroEyebrow: 'Obukuumi bw’Eby’Obugagga Eby’Ebiseera eby’omu Maaso',
      heroTitle: 'Okukuuma Eby’Obugagga Ebikulu. Okutebenkeza Ebiseera by’e Ggwanga.',
      heroDesc: 'Okuva ku masanduuko g’ebyama ag’omulembe okutuuka ku mmotoka ezitambuza sente ne zaabu (CIT) n’obukuumi bwa kamera za AI obwa saawa 24 mu Uganda.',
    },
    'fr-FR': {
      title: 'Sovereign Asset Defense | Protection d’Actifs Institutionnels et Coffres Privés',
      desc: 'Coffres-forts ultra-sécurisés, transport de fonds blindé (CIT) et surveillance IA 24/7 pour les secteurs stratégiques en Ouganda.',
      ogTitle: 'Sovereign Asset Defense Ouganda',
      ogDesc: 'Protéger les Actifs Critiques. Sécuriser l’Avenir de l’Ouganda.',
      heroEyebrow: 'Sécurité Institutionnelle et Protection d’Actifs',
      heroTitle: 'Protéger les Actifs Critiques. Sécuriser l’Avenir de l’Ouganda.',
      heroDesc: 'Des coffres-forts privés ultra-sécurisés au transport de fonds blindé (CIT) et à la surveillance IA 24/7 pour les secteurs stratégiques en Ouganda.',
    },
    'ar-SA': {
      title: 'الدفاع عن الأصول السيادية | حماية الأصول المؤسسية والخزائن الخاصة',
      desc: 'خزائن خاصة فائقة الأمان بمعايير مصرفية، لوجستيات سبائك ونقد مصفحة (CIT)، ومراقبة ذكية عبر مركز العمليات SOC على مدار الساعة في أوغندا.',
      ogTitle: 'الدفاع عن الأصول السيادية أوغندا',
      ogDesc: 'حماية الأصول الحيوية. تأمين مستقبل أوغندا الاقتصادي.',
      heroEyebrow: 'الأمن المؤسسي وحماية الأصول الاستراتيجية',
      heroTitle: 'حماية الأصول الحيوية. تأمين مستقبل أوغندا.',
      heroDesc: 'من خزائن الودائع المصرفية الخاصة وفائقة الأمان إلى لوجستيات السبائك المصفحة وحراسة الذكاء الاصطناعي على مدار الساعة.',
    },
    'zh-CN': {
      title: 'Sovereign 机构主权资产防务 | 私人金库与武装押运',
      desc: '银行级私人高安金库托管、装甲贵金属现钞押运 (CIT)，以及面向乌干达战略经济前沿的24/7全天候AI智能安防指挥中心。',
      ogTitle: 'Sovereign 机构资产防务 乌干达',
      ogDesc: '守护关键资产，捍卫乌干达经济未来。',
      heroEyebrow: '机构级安防与战略资产保护',
      heroTitle: '守护关键资产，捍卫乌干达经济未来。',
      heroDesc: '从银行级私人高安金库保管、装甲贵金属与现钞押运，到24/7全天候人工智能安防指挥中心。',
    },
  };

  /**
   * Compiles a standalone HTML page with selective DTCG themes, semantic i18n dictionaries,
   * all 7 content sections populated with media images, and zero-runtime client-side switcher script.
   */
  public static renderToStandaloneHtml(
    config: LocalizedLandingConfig,
    exportPolicy?: StandaloneExportPolicy
  ): string {
    const locale: SupportedLocale = (config.activeLocale || config.defaultLocale || exportPolicy?.defaultLocale || 'en-US') as SupportedLocale;
    const activeThemeId = config.defaultThemeId ?? exportPolicy?.defaultThemeId ?? 'sovereign-dark';
    const publishedLocales = config.publishedLocales || (exportPolicy?.includedLocales as SupportedLocale[]) || ['en-US', 'sw-KE', 'lg-UG', 'fr-FR', 'ar-SA', 'zh-CN'];
    const isRtl = locale === 'ar-SA' || locale.startsWith('ar');

    const meta = this.METADATA_DICTIONARIES[locale] ?? this.METADATA_DICTIONARIES['en-US'];
    const base = (config.baseDomain ?? 'https://sovereign.africa').replace(/\/+$/, '');
    const canonicalUrl = `${base}/${locale.split('-')[0]}/security/`;

    const allThemes: Record<string, ThemeDefinition> = {
      'sovereign-dark': SOVEREIGN_DARK,
      'sovereign-light': SOVEREIGN_LIGHT,
      'midnight-nordic': MIDNIGHT_NORDIC,
      'industrial-amber': INDUSTRIAL_AMBER,
      'high-contrast': HIGH_CONTRAST,
      'emerald-matrix': EMERALD_MATRIX,
      'cyberpunk-neon': CYBERPUNK_NEON,
    };

    const includedThemeIds = exportPolicy?.includedThemeIds ?? Object.keys(allThemes);
    const compiledThemeCssMap: Record<string, Record<string, string>> = {};
    for (const themeId of includedThemeIds) {
      if (allThemes[themeId]) {
        compiledThemeCssMap[themeId] = DtcgCssCompiler.compileToCssVariables(allThemes[themeId]);
      }
    }

    const includedLocales = exportPolicy?.includedLocales ?? ['en-US', 'sw-KE', 'lg-UG', 'fr-FR', 'ar-SA', 'zh-CN'];

    const exportedDictionaries: Record<string, Record<string, string>> = {};
    for (const loc of includedLocales) {
      if (CANONICAL_I18N_DICTIONARIES[loc]) {
        exportedDictionaries[loc] = CANONICAL_I18N_DICTIONARIES[loc];
      }
    }

    const initialTheme = allThemes[activeThemeId] ?? SOVEREIGN_DARK;
    const initialCssVars = DtcgCssCompiler.compileToAotCss(initialTheme);

    const hreflangTags = publishedLocales
      .map((loc) => `<link rel="alternate" hreflang="${loc}" href="${base}/${loc.split('-')[0]}/security/" />`)
      .join('\n  ');

    // Build and project the 7-section page tree across the active locale
    const rawPageTree = config.pageTree ?? createSecurityCompanyLandingPageTree();
    const targetPageTree = SemanticLandingTranslator.projectLocalizedPageTree(rawPageTree, locale);

    // Render all 7 sections dynamically
    let sectionsHtml = '';
    for (const section of targetPageTree.sections) {
      let blocksHtml = '';

      for (const block of section.blocks) {
        if (!block.isVisible) continue;

        switch (block.type) {
          case 'hero_banner': {
            const heroB = block as CmsHeroBannerBlock;
            // Support localized alt and overrides
            const heroBlockWithMedia: CmsHeroBannerBlock = {
              ...heroB,
              payload: {
                ...heroB.payload,
                imageUrl: config.heroMediaAttachment?.media.publicUrl || config.heroImageUrl || heroB.payload.imageUrl || 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&auto=format&fit=crop&q=80',
                imageAltText: config.heroMediaAttachment?.localizedAltText[locale] || config.heroMediaAttachment?.media.altText || heroB.payload.imageAltText || meta.title,
                heroMediaAttachment: config.heroMediaAttachment || heroB.payload.heroMediaAttachment,
              },
            };
            blocksHtml += SecurityLandingRenderer.renderHero(heroBlockWithMedia);
            break;
          }
          case 'comparison_matrix':
            blocksHtml += SecurityLandingRenderer.renderComparison(block as CmsComparisonMatrixBlock);
            break;
          case 'service_catalog':
            blocksHtml += SecurityLandingRenderer.renderServices(block as CmsServiceCatalogBlock);
            break;
          case 'tech_engine':
            blocksHtml += SecurityLandingRenderer.renderTechEngine(block as CmsTechEngineBlock);
            break;
          case 'industry_matrix':
            blocksHtml += SecurityLandingRenderer.renderIndustryMatrix(block as CmsIndustryMatrixBlock);
            break;
          case 'trust_audit':
            blocksHtml += SecurityLandingRenderer.renderTrustAudit(block as CmsTrustAuditBlock);
            break;
          case 'lead_capture_form':
            blocksHtml += SecurityLandingRenderer.renderLeadForm(block as CmsLeadCaptureFormBlock);
            break;
        }
      }

      const sectionClass = `sec-${section.sectionId} theme-${section.themeVariant || 'default'} layout-${section.layout}`;
      sectionsHtml += `<section id="${section.sectionId}" class="${sectionClass}">${blocksHtml}</section>\n`;
    }


    const brandName = config.brandName || 'SOVEREIGN ASSET DEFENSE';

    return `<!DOCTYPE html>
<html lang="${locale}" dir="${isRtl ? 'rtl' : 'ltr'}" data-theme="${activeThemeId}">
<head>

  <meta charset="UTF-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self' 'unsafe-inline' blob: data: http: https:; script-src 'self' 'unsafe-inline' blob:; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' data: https://fonts.gstatic.com; img-src 'self' data: blob: http: https:; frame-src 'self' blob: data: http: https:;">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${config.title || meta.title}</title>
  <meta name="description" content="${meta.desc}">
  <link rel="canonical" href="${canonicalUrl}">
  ${hreflangTags}
  <link rel="alternate" hreflang="x-default" href="${base}/en/security/" />

  <!-- Open Graph / Social -->
  <meta property="og:type" content="website">
  <meta property="og:url" content="${canonicalUrl}">
  <meta property="og:title" content="${meta.ogTitle}">
  <meta property="og:description" content="${meta.ogDesc}">
  <meta property="og:locale" content="${locale}">
  ${config.heroMediaAttachment ? `<meta property="og:image" content="${config.heroMediaAttachment.media.publicUrl}">` : ''}

  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Outfit:wght@600;700;800&display=swap" rel="stylesheet">

  <script>
    function handleNavLangChange(targetLocale) {
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'CMS_LOCALE_CHANGE', locale: targetLocale }, '*');
        }
      } catch (e) {}
      var langCode = targetLocale.split('-')[0];
      if (!window.location.href.startsWith('about:')) {
        window.location.href = '/' + langCode + '/security/';
      }
    }

    document.addEventListener('click', function(e) {
      var anchor = e.target.closest('a[href^="#"]');
      if (anchor) {
        var href = anchor.getAttribute('href');
        if (href && href.length > 1) {
          var targetId = href.substring(1);
          var targetEl = document.getElementById(targetId);
          if (targetEl) {
            e.preventDefault();
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            try { history.pushState(null, '', '#' + targetId); } catch (err) {}
          }
        }
      }
    });
  </script>

  <style>
    ${initialCssVars}
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body {
      background-color: var(--surface-canvas, #090e17);
      color: var(--text-primary, #f8fafc);
      font-family: var(--font-sans, 'Inter', sans-serif);
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
      text-align: start;
    }
    html[lang="zh-CN"] body {
      font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
      line-height: 1.75;
    }
    html[dir="rtl"] {
      text-align: start;
    }
    h1, h2, h3, h4 { font-family: 'Outfit', sans-serif; font-weight: 700; color: #ffffff; letter-spacing: -0.02em; }
    html[lang="zh-CN"] h1, html[lang="zh-CN"] h2, html[lang="zh-CN"] h3, html[lang="zh-CN"] h4 { font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif; letter-spacing: normal; }
    a { color: var(--brand-primary, #38bdf8); text-decoration: none; transition: color 0.2s; }
    
    /* Navigation Bar */
    .navbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-inline: 2rem;
      padding-block: 1.25rem;
      background: var(--surface-panel, rgba(9, 14, 23, 0.85));
      backdrop-filter: blur(12px);
      border-block-end: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08));
      position: sticky;
      top: 0;
      z-index: 100;
    }
    .nav-brand { font-family: 'Outfit', sans-serif; font-size: 1.25rem; font-weight: 800; color: #fff; display: flex; align-items: center; gap: 0.5rem; }
    .nav-brand-badge { background: var(--brand-primary, #0284c7); color: var(--brand-on-primary, #fff); font-size: 0.65rem; padding: 2px 6px; border-radius: 4px; text-transform: uppercase; font-weight: 700; }
    .nav-links { display: flex; gap: 1rem; align-items: center; list-style: none; flex-wrap: wrap; }
    .nav-links a { font-size: 0.85rem; font-weight: 600; color: var(--text-secondary, #94a3b8); }
    .nav-links a:hover { color: #fff; }
    .btn-nav { background: var(--brand-primary, #0284c7); color: var(--brand-on-primary, #fff) !important; padding: 0.4rem 0.9rem; border-radius: var(--radius-control, 6px); font-weight: 700; font-size: 0.85rem; }
    .btn-nav:hover { opacity: 0.9; }
    .nav-lang-select {
      background: var(--surface-card, rgba(255, 255, 255, 0.08));
      color: var(--text-primary, #f8fafc);
      border: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.15));
      border-radius: var(--radius-control, 6px);
      padding: 0.3rem 0.55rem;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      outline: none;
      transition: border-color 0.2s;
    }
    .nav-lang-select:hover { border-color: var(--brand-primary, #38bdf8); }
    .nav-lang-select option { background: #090e17; color: #fff; }

    /* Layout & Sections */
    section { padding-inline: 2rem; padding-block: 5rem; border-block-end: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08)); }
    .layout-contained { max-width: 1200px; margin-inline: auto; }
    .sec-header { text-align: center; max-width: 750px; margin-inline: auto; margin-block-end: 3.5rem; }
    .sec-header h2 { font-size: 2.25rem; margin-block-end: 0.75rem; }
    .sec-header p { color: var(--text-secondary, #94a3b8); font-size: 1.1rem; }

    /* Section 1: Hero Banner */
    .hero-container { max-width: 1200px; margin-inline: auto; padding-block: 2rem; text-align: center; }
    .hero-eyebrow { display: inline-block; color: var(--brand-primary, #38bdf8); font-size: 0.85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-block-end: 1rem; background: rgba(56, 189, 248, 0.1); padding: 4px 12px; border-radius: 20px; border: 1px solid var(--border-strong, rgba(56, 189, 248, 0.3)); }
    .hero-title { font-size: clamp(2.5rem, 5vw, 3.8rem); line-height: 1.15; margin-block-end: 1.5rem; max-width: 960px; margin-inline: auto; }
    .hero-desc { font-size: 1.25rem; color: var(--text-secondary, #94a3b8); margin-block-end: 2.5rem; max-width: 780px; margin-inline: auto; }
    .hero-ctas { display: flex; gap: 1rem; margin-block-end: 2.5rem; justify-content: center; flex-wrap: wrap; }
    .btn-primary { background: linear-gradient(135deg, var(--brand-primary, #0284c7) 0%, var(--brand-primary-hover, #0369a1) 100%); color: var(--brand-on-primary, #fff); font-weight: 700; padding: 0.9rem 2rem; border-radius: var(--radius-card, 12px); font-size: 1rem; box-shadow: 0 4px 20px rgba(2, 132, 199, 0.4); }
    .btn-secondary { background: rgba(255, 255, 255, 0.05); color: #fff; font-weight: 600; padding: 0.9rem 2rem; border-radius: var(--radius-card, 12px); font-size: 1rem; border: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08)); }
    .btn-secondary:hover { background: rgba(255, 255, 255, 0.1); }
    .trust-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; padding-block-start: 2.5rem; border-block-start: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08)); }
    .trust-badge-item { display: flex; align-items: center; justify-content: center; gap: 0.75rem; color: var(--text-secondary, #94a3b8); font-size: 0.9rem; font-weight: 600; }
    .trust-badge-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent-success, #10b981); box-shadow: 0 0 8px var(--accent-success, #10b981); }

    /* Section 2: Comparison Matrix */
    .comparison-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; margin-block-start: 2rem; }
    .comp-card { background: var(--surface-panel, rgba(17, 26, 46, 0.75)); border-radius: var(--radius-card, 12px); padding: 2.5rem; border: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08)); }
    .comp-card.legacy { border-inline-start: 4px solid var(--accent-danger, #ef4444); background: rgba(30, 20, 25, 0.4); }
    .comp-card.modern { border-inline-start: 4px solid var(--brand-primary, #38bdf8); background: rgba(14, 30, 50, 0.6); box-shadow: 0 8px 30px rgba(0,0,0,0.3); }
    .comp-header { font-size: 1.4rem; font-weight: 700; margin-block-end: 1.5rem; display: flex; justify-content: space-between; align-items: center; }
    .comp-list { list-style: none; display: flex; flex-direction: column; gap: 1rem; }
    .comp-item { display: flex; align-items: flex-start; gap: 0.75rem; font-size: 0.95rem; }
    .icon-fail { color: var(--accent-danger, #ef4444); font-weight: bold; }
    .icon-pass { color: var(--accent-success, #10b981); font-weight: bold; }

    /* Section 3: Service Pillars */
    .services-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 1.5rem; }
    .service-card { background: var(--surface-panel, rgba(17, 26, 46, 0.75)); border: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08)); border-radius: var(--radius-card, 12px); padding: 2rem; transition: transform 0.2s, border-color 0.2s; }
    .service-card:hover { transform: translateY(-4px); border-color: var(--border-strong, #38bdf8); }
    .service-target { font-size: 0.75rem; text-transform: uppercase; color: var(--accent-warning, #f59e0b); font-weight: 700; margin-block-end: 0.5rem; letter-spacing: 0.05em; }
    .service-title { font-size: 1.3rem; margin-block-end: 0.75rem; }
    .service-desc { color: var(--text-secondary, #94a3b8); font-size: 0.9rem; margin-block-end: 1.25rem; }
    .service-features { list-style: none; display: flex; flex-direction: column; gap: 0.5rem; padding-block-start: 1rem; border-block-start: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08)); }
    .service-features li { font-size: 0.85rem; color: var(--text-secondary, #94a3b8); display: flex; align-items: center; gap: 0.5rem; }
    .service-features li::before { content: "✓"; color: var(--brand-primary, #38bdf8); font-weight: bold; }

    /* Section 4: Tech Engine */
    .tech-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 1.5rem; margin-block-start: 2rem; }
    .tech-card { background: var(--surface-panel-subtle, rgba(2, 6, 23, 0.7)); border: 1px solid var(--border-strong, rgba(56, 189, 248, 0.3)); border-radius: var(--radius-card, 12px); padding: 2rem; }
    .tech-metric { display: inline-block; background: rgba(56, 189, 248, 0.15); color: var(--brand-primary, #38bdf8); font-size: 0.8rem; font-weight: 700; padding: 2px 8px; border-radius: 4px; margin-block-end: 1rem; }
    .tech-title { font-size: 1.15rem; margin-block-end: 0.75rem; }
    .tech-desc { color: var(--text-secondary, #94a3b8); font-size: 0.85rem; margin-block-end: 1rem; }
    .tech-bullets { list-style: none; display: flex; flex-direction: column; gap: 0.4rem; font-size: 0.8rem; color: var(--text-secondary, #cbd5e1); }
    .tech-bullets li::before { content: "▸ "; color: var(--brand-primary, #38bdf8); }

    /* Section 5: Industry Matrix */
    .industry-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; }
    .industry-card { background: var(--surface-panel, rgba(17, 26, 46, 0.75)); border-radius: var(--radius-card, 12px); padding: 1.75rem; border: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08)); }
    .industry-name { font-size: 1.15rem; font-weight: 700; color: #fff; margin-block-end: 0.5rem; }
    .industry-risk { color: var(--text-secondary, #94a3b8); font-size: 0.85rem; margin-block-end: 1rem; }
    .industry-services-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; }
    .service-tag { background: rgba(255, 255, 255, 0.05); color: var(--brand-primary, #38bdf8); font-size: 0.75rem; padding: 2px 8px; border-radius: 4px; }

    /* Section 6: Trust Audit */
    .trust-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem; }
    .trust-card { background: var(--surface-panel, rgba(17, 26, 46, 0.75)); border-radius: var(--radius-card, 12px); padding: 2rem; border-block-start: 3px solid var(--accent-warning, #f59e0b); }
    .trust-standard { color: var(--accent-warning, #f59e0b); font-size: 0.75rem; font-weight: 800; text-transform: uppercase; margin-block-end: 0.5rem; }
    .trust-title { font-size: 1.15rem; margin-block-end: 0.75rem; }
    .trust-desc { color: var(--text-secondary, #94a3b8); font-size: 0.85rem; }

    /* Section 7: Lead Form */
    .lead-container { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; background: var(--surface-panel, rgba(17, 26, 46, 0.75)); border-radius: var(--radius-card, 16px); padding: 3.5rem; border: 1px solid var(--border-strong, rgba(56, 189, 248, 0.3)); }
    .lead-info h2 { font-size: 2.2rem; margin-block-end: 1rem; }
    .lead-info p { color: var(--text-secondary, #94a3b8); margin-block-end: 2rem; }
    .office-card { background: rgba(0,0,0,0.3); border-radius: var(--radius-card, 12px); padding: 1.5rem; border: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08)); }
    .office-card h4 { font-size: 1rem; color: var(--brand-primary, #38bdf8); margin-block-end: 0.5rem; }
    .office-card p { font-size: 0.85rem; color: var(--text-secondary, #94a3b8); margin-block-end: 0.25rem; }

    .form-box { display: flex; flex-direction: column; gap: 1rem; }
    .form-group { display: flex; flex-direction: column; gap: 0.4rem; }
    .form-group label { font-size: 0.85rem; font-weight: 600; color: var(--text-secondary, #cbd5e1); }
    .form-group input, .form-group select, .form-group textarea {
      background: var(--surface-panel-subtle, rgba(15, 23, 42, 0.9));
      border: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08));
      border-radius: var(--radius-control, 6px);
      padding: 0.75rem 1rem;
      color: #fff;
      font-size: 0.9rem;
      outline: none;
      transition: border-color 0.2s;
    }
    .form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color: var(--brand-primary, #38bdf8); }
    .form-consent { font-size: 0.75rem; color: var(--text-muted, #64748b); display: flex; gap: 0.5rem; align-items: flex-start; }
    .btn-submit { background: var(--brand-primary, #0284c7); color: var(--brand-on-primary, #fff); font-weight: 700; padding: 1rem; border: none; border-radius: var(--radius-control, 6px); font-size: 1rem; cursor: pointer; transition: opacity 0.2s; }
    .btn-submit:hover { opacity: 0.9; }

    /* Mirrored and Isolated Bidirectional Styles */
    html[dir="rtl"] .icon-chevron-forward { transform: scaleX(-1); }
    .ltr-isolated { text-align: left; unicode-bidi: isolate; }
    .tech-token { unicode-bidi: isolate; display: inline-block; }

    /* Footer */
    footer { padding-block: 3rem; padding-inline: 2rem; background: var(--surface-canvas, #050810); border-block-start: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.08)); color: var(--text-muted, #64748b); font-size: 0.85rem; text-align: center; }
    .footer-links { display: flex; justify-content: center; gap: 1.5rem; margin-block-end: 1rem; }

    @media (max-width: 900px) {
      .hero-title { font-size: 2.5rem; }
      .comparison-grid, .lead-container { grid-template-columns: 1fr; }
      .navbar { flex-direction: column; gap: 1rem; }
    }
  </style>

</head>
<body>

  <!-- Navigation Bar -->
  <header class="navbar">
    <div class="nav-brand">
      🛡️ ${brandName}
      <span class="nav-brand-badge">Uganda HQ</span>
    </div>
    <nav>
      <ul class="nav-links">
        <li><a href="#sec-service-pillars">Services</a></li>
        <li><a href="#sec-tech-engine">Tech</a></li>
        <li><a href="#sec-industry-matrix">Industries</a></li>
        <li><a href="#sec-trust-governance">Trust</a></li>
        <li><a href="#sec-lead-capture" class="btn-nav">Assess</a></li>
        <li class="nav-lang-item">
          <select onchange="handleNavLangChange(this.value)" aria-label="Select Language" class="nav-lang-select">
            ${publishedLocales.map((loc) => {
              const code = loc.split('-')[0].toUpperCase();
              const isSelected = loc === locale;
              return `<option value="${loc}" ${isSelected ? 'selected' : ''}>🌐 ${code}</option>`;
            }).join('')}
          </select>
        </li>
      </ul>
    </nav>
  </header>

  <main>
    ${sectionsHtml}
  </main>

  <footer>
    <div class="footer-links">
      <a href="#sec-hero">Overview</a>
      <a href="#sec-service-pillars">Private Vault Custody</a>
      <a href="#sec-service-pillars">Cash-in-Transit (CIT)</a>
      <a href="#sec-lead-capture">Kampala Operations SOC</a>
    </div>
    <p>© 2026 ${brandName}. Regulated & Certified Institutional Asset Protection Platform. Kampala, Uganda.</p>
  </footer>

  <script>
    const themeDefinitions = ${JSON.stringify(compiledThemeCssMap)};
    const dictionaries = ${JSON.stringify(exportedDictionaries)};
  </script>
</body>
</html>`;
  }

  public static renderToHtml(pageTree: CmsPageTree, brandName = 'SOVEREIGN ASSET DEFENSE'): string {
    let sectionsHtml = '';

    for (const section of pageTree.sections) {
      let blocksHtml = '';

      for (const block of section.blocks) {
        if (!block.isVisible) continue;

        switch (block.type) {
          case 'hero_banner':
            blocksHtml += SecurityLandingRenderer.renderHero(block as CmsHeroBannerBlock);
            break;
          case 'comparison_matrix':
            blocksHtml += SecurityLandingRenderer.renderComparison(block as CmsComparisonMatrixBlock);
            break;
          case 'service_catalog':
            blocksHtml += SecurityLandingRenderer.renderServices(block as CmsServiceCatalogBlock);
            break;
          case 'tech_engine':
            blocksHtml += SecurityLandingRenderer.renderTechEngine(block as CmsTechEngineBlock);
            break;
          case 'industry_matrix':
            blocksHtml += SecurityLandingRenderer.renderIndustryMatrix(block as CmsIndustryMatrixBlock);
            break;
          case 'trust_audit':
            blocksHtml += SecurityLandingRenderer.renderTrustAudit(block as CmsTrustAuditBlock);
            break;
          case 'lead_capture_form':
            blocksHtml += SecurityLandingRenderer.renderLeadForm(block as CmsLeadCaptureFormBlock);
            break;
        }
      }

      const sectionClass = `sec-${section.sectionId} theme-${section.themeVariant || 'default'} layout-${section.layout}`;
      sectionsHtml += `<section id="${section.sectionId}" class="${sectionClass}">${blocksHtml}</section>\n`;
    }

    return `
<!DOCTYPE html>
<html lang="en" dir="ltr">
<head>
  <meta charset="UTF-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self' 'unsafe-inline' blob: data: http: https:; script-src 'self' 'unsafe-inline' blob:; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' data: https://fonts.gstatic.com; img-src 'self' data: blob: http: https:; frame-src 'self' blob: data: http: https:;">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${brandName} | Institutional Security, Private Vaults & CIT Uganda</title>
  <meta name="description" content="Bank-grade private vaults, armored cash-in-transit, AI surveillance SOC, and industrial mining & oil security in Kampala, Uganda.">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Outfit:wght@600;700;800&display=swap" rel="stylesheet">
  <script>
    document.addEventListener('click', function(e) {
      var anchor = e.target.closest('a[href^="#"]');
      if (anchor) {
        var href = anchor.getAttribute('href');
        if (href && href.length > 1) {
          var targetId = href.substring(1);
          var targetEl = document.getElementById(targetId);
          if (targetEl) {
            e.preventDefault();
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            try { history.pushState(null, '', '#' + targetId); } catch (err) {}
          }
        }
      }
    });
  </script>
  <style>
    html { scroll-behavior: smooth; }
    :root {
      --bg-primary: #090e17;
      --bg-surface: #111a2e;
      --bg-card: rgba(17, 26, 46, 0.75);
      --bg-card-hover: rgba(26, 38, 66, 0.9);
      --text-primary: #f8fafc;
      --text-secondary: #94a3b8;
      --text-muted: #64748b;
      --brand-accent: #0284c7;
      --brand-glow: #38bdf8;
      --gold-accent: #f59e0b;
      --danger-accent: #ef4444;
      --success-accent: #10b981;
      --border-subtle: rgba(255, 255, 255, 0.08);
      --border-accent: rgba(56, 189, 248, 0.3);
      --radius-sm: 6px;
      --radius-md: 12px;
      --radius-lg: 20px;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      background-color: var(--bg-primary);
      color: var(--text-primary);
      font-family: 'Inter', sans-serif;
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
    }
    h1, h2, h3, h4 { font-family: 'Outfit', sans-serif; font-weight: 700; color: #ffffff; letter-spacing: -0.02em; }
    a { color: var(--brand-glow); text-decoration: none; transition: color 0.2s; }
    
    /* Navigation Bar */
    .navbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.25rem 2rem;
      background: rgba(9, 14, 23, 0.85);
      backdrop-filter: blur(12px);
      border-bottom: 1px solid var(--border-subtle);
      position: sticky;
      top: 0;
      z-index: 100;
    }
    .nav-brand { font-family: 'Outfit', sans-serif; font-size: 1.25rem; font-weight: 800; color: #fff; display: flex; align-items: center; gap: 0.5rem; }
    .nav-brand-badge { background: var(--brand-accent); color: #fff; font-size: 0.65rem; padding: 2px 6px; border-radius: 4px; text-transform: uppercase; }
    .nav-links { display: flex; gap: 1rem; align-items: center; list-style: none; flex-wrap: wrap; }
    .nav-links a { font-size: 0.85rem; font-weight: 600; color: var(--text-secondary); }
    .nav-links a:hover { color: #fff; }
    .btn-nav { background: var(--brand-accent); color: #fff !important; padding: 0.4rem 0.9rem; border-radius: var(--radius-sm); font-weight: 700; font-size: 0.85rem; }
    .btn-nav:hover { background: #0369a1; }

    /* Layout & Sections */
    section { padding: 5rem 2rem; border-bottom: 1px solid var(--border-subtle); }
    .layout-contained { max-width: 1200px; margin: 0 auto; }
    .sec-header { text-align: center; max-width: 750px; margin: 0 auto 3.5rem; }
    .sec-header h2 { font-size: 2.25rem; margin-bottom: 0.75rem; }
    .sec-header p { color: var(--text-secondary); font-size: 1.1rem; }

    /* Section 1: Hero Banner */
    .hero-container { max-width: 1200px; margin: 0 auto; padding: 2rem 0; text-align: center; }
    .hero-eyebrow { display: inline-block; color: var(--brand-glow); font-size: 0.85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 1rem; background: rgba(56, 189, 248, 0.1); padding: 4px 12px; border-radius: 20px; border: 1px solid var(--border-accent); }
    .hero-title { font-size: clamp(2.5rem, 5vw, 3.8rem); line-height: 1.15; margin-bottom: 1.5rem; max-width: 960px; margin-left: auto; margin-right: auto; }
    .hero-desc { font-size: 1.25rem; color: var(--text-secondary); margin-bottom: 2.5rem; max-width: 780px; margin-left: auto; margin-right: auto; }
    .hero-ctas { display: flex; gap: 1rem; margin-bottom: 2.5rem; justify-content: center; flex-wrap: wrap; }
    .btn-primary { background: linear-gradient(135deg, var(--brand-accent) 0%, #0284c7 100%); color: #fff; font-weight: 700; padding: 0.9rem 2rem; border-radius: var(--radius-md); font-size: 1rem; box-shadow: 0 4px 20px rgba(2, 132, 199, 0.4); }
    .btn-secondary { background: rgba(255, 255, 255, 0.05); color: #fff; font-weight: 600; padding: 0.9rem 2rem; border-radius: var(--radius-md); font-size: 1rem; border: 1px solid var(--border-subtle); }
    .btn-secondary:hover { background: rgba(255, 255, 255, 0.1); }
    .trust-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; padding-top: 2.5rem; border-top: 1px solid var(--border-subtle); }
    .trust-badge-item { display: flex; align-items: center; justify-content: center; gap: 0.75rem; color: var(--text-secondary); font-size: 0.9rem; font-weight: 600; }
    .trust-badge-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--success-accent); box-shadow: 0 0 8px var(--success-accent); }

    /* Section 2: Comparison Matrix */
    .comparison-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; margin-top: 2rem; }
    .comp-card { background: var(--bg-card); border-radius: var(--radius-lg); padding: 2.5rem; border: 1px solid var(--border-subtle); }
    .comp-card.legacy { border-left: 4px solid var(--danger-accent); background: rgba(30, 20, 25, 0.4); }
    .comp-card.modern { border-left: 4px solid var(--brand-glow); background: rgba(14, 30, 50, 0.6); box-shadow: 0 8px 30px rgba(0,0,0,0.3); }
    .comp-header { font-size: 1.4rem; font-weight: 700; margin-bottom: 1.5rem; display: flex; justify-content: space-between; align-items: center; }
    .comp-list { list-style: none; display: flex; flex-direction: column; gap: 1rem; }
    .comp-item { display: flex; align-items: flex-start; gap: 0.75rem; font-size: 0.95rem; }
    .icon-fail { color: var(--danger-accent); font-weight: bold; }
    .icon-pass { color: var(--success-accent); font-weight: bold; }

    /* Section 3: Service Pillars */
    .services-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 1.5rem; }
    .service-card { background: var(--bg-card); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 2rem; transition: transform 0.2s, border-color 0.2s; }
    .service-card:hover { transform: translateY(-4px); border-color: var(--border-accent); }
    .service-target { font-size: 0.75rem; text-transform: uppercase; color: var(--gold-accent); font-weight: 700; margin-bottom: 0.5rem; letter-spacing: 0.05em; }
    .service-title { font-size: 1.3rem; margin-bottom: 0.75rem; }
    .service-desc { color: var(--text-secondary); font-size: 0.9rem; margin-bottom: 1.25rem; }
    .service-features { list-style: none; display: flex; flex-direction: column; gap: 0.5rem; padding-top: 1rem; border-top: 1px solid var(--border-subtle); }
    .service-features li { font-size: 0.85rem; color: var(--text-secondary); display: flex; align-items: center; gap: 0.5rem; }
    .service-features li::before { content: "✓"; color: var(--brand-glow); font-weight: bold; }

    /* Section 4: Tech Engine */
    .tech-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 1.5rem; margin-top: 2rem; }
    .tech-card { background: rgba(2, 6, 23, 0.7); border: 1px solid var(--border-accent); border-radius: var(--radius-md); padding: 2rem; }
    .tech-metric { display: inline-block; background: rgba(56, 189, 248, 0.15); color: var(--brand-glow); font-size: 0.8rem; font-weight: 700; padding: 2px 8px; border-radius: 4px; margin-bottom: 1rem; }
    .tech-title { font-size: 1.15rem; margin-bottom: 0.75rem; }
    .tech-desc { color: var(--text-secondary); font-size: 0.85rem; margin-bottom: 1rem; }
    .tech-bullets { list-style: none; display: flex; flex-direction: column; gap: 0.4rem; font-size: 0.8rem; color: #cbd5e1; }
    .tech-bullets li::before { content: "▸ "; color: var(--brand-glow); }

    /* Section 5: Industry Matrix */
    .industry-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; }
    .industry-card { background: var(--bg-card); border-radius: var(--radius-md); padding: 1.75rem; border: 1px solid var(--border-subtle); }
    .industry-name { font-size: 1.15rem; font-weight: 700; color: #fff; margin-bottom: 0.5rem; }
    .industry-risk { color: var(--text-secondary); font-size: 0.85rem; margin-bottom: 1rem; }
    .industry-services-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; }
    .service-tag { background: rgba(255, 255, 255, 0.05); color: var(--brand-glow); font-size: 0.75rem; padding: 2px 8px; border-radius: 4px; }

    /* Section 6: Trust Audit */
    .trust-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem; }
    .trust-card { background: var(--bg-card); border-radius: var(--radius-md); padding: 2rem; border-top: 3px solid var(--gold-accent); }
    .trust-standard { color: var(--gold-accent); font-size: 0.75rem; font-weight: 800; text-transform: uppercase; margin-bottom: 0.5rem; }
    .trust-title { font-size: 1.15rem; margin-bottom: 0.75rem; }
    .trust-desc { color: var(--text-secondary); font-size: 0.85rem; }

    /* Section 7: Lead Form */
    .lead-container { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; background: var(--bg-card); border-radius: var(--radius-lg); padding: 3.5rem; border: 1px solid var(--border-accent); }
    .lead-info h2 { font-size: 2.2rem; margin-bottom: 1rem; }
    .lead-info p { color: var(--text-secondary); margin-bottom: 2rem; }
    .office-card { background: rgba(0,0,0,0.3); border-radius: var(--radius-md); padding: 1.5rem; border: 1px solid var(--border-subtle); }
    .office-card h4 { font-size: 1rem; color: var(--brand-glow); margin-bottom: 0.5rem; }
    .office-card p { font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 0.25rem; }

    .form-box { display: flex; flex-direction: column; gap: 1rem; }
    .form-group { display: flex; flex-direction: column; gap: 0.4rem; }
    .form-group label { font-size: 0.85rem; font-weight: 600; color: #cbd5e1; }
    .form-group input, .form-group select, .form-group textarea {
      background: rgba(15, 23, 42, 0.9);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-sm);
      padding: 0.75rem 1rem;
      color: #fff;
      font-size: 0.9rem;
      outline: none;
      transition: border-color 0.2s;
    }
    .form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color: var(--brand-glow); }
    .form-consent { font-size: 0.75rem; color: var(--text-muted); display: flex; gap: 0.5rem; align-items: flex-start; }
    .btn-submit { background: var(--brand-accent); color: #fff; font-weight: 700; padding: 1rem; border: none; border-radius: var(--radius-sm); font-size: 1rem; cursor: pointer; transition: background 0.2s; }
    .btn-submit:hover { background: #0369a1; }

    /* Footer */
    footer { padding: 3rem 2rem; background: #050810; border-top: 1px solid var(--border-subtle); color: var(--text-muted); font-size: 0.85rem; text-align: center; }
    .footer-links { display: flex; justify-content: center; gap: 1.5rem; margin-bottom: 1rem; }

    @media (max-width: 900px) {
      .hero-title { font-size: 2.5rem; }
      .comparison-grid, .lead-container { grid-template-columns: 1fr; }
      .navbar { flex-direction: column; gap: 1rem; }
    }
  </style>
</head>
<body>

  <!-- Navigation Bar -->
  <header class="navbar">
    <div class="nav-brand">
      🛡️ ${brandName}
      <span class="nav-brand-badge">Uganda HQ</span>
    </div>
    <nav>
      <ul class="nav-links">
        <li><a href="#sec-service-pillars">Services</a></li>
        <li><a href="#sec-tech-engine">Tech</a></li>
        <li><a href="#sec-industry-matrix">Industries</a></li>
        <li><a href="#sec-trust-governance">Trust</a></li>
        <li><a href="#sec-lead-capture" class="btn-nav">Assess</a></li>
      </ul>
    </nav>
  </header>

  <main>
    ${sectionsHtml}
  </main>

  <footer>
    <div class="footer-links">
      <a href="#sec-hero">Overview</a>
      <a href="#sec-service-pillars">Private Vault Custody</a>
      <a href="#sec-service-pillars">Cash-in-Transit (CIT)</a>
      <a href="#sec-lead-capture">Kampala Operations SOC</a>
    </div>
    <p>© 2026 ${brandName}. Regulated & Certified Institutional Asset Protection Platform. Kampala, Uganda.</p>
  </footer>

</body>
</html>
    `.trim();
  }

  private static renderHero(block: CmsHeroBannerBlock): string {
    const { eyebrow, title, description, primaryCta, secondaryCta, trustBadges, imageUrl, heroMediaAttachment } = block.payload;
    const badgesHtml = trustBadges
      ? trustBadges
          .map(
            (b) =>
              `<div class="trust-badge-item"><span class="trust-badge-dot"></span><span>${b}</span></div>`
          )
          .join('')
      : '';

    const heroImgSrc = heroMediaAttachment?.media.publicUrl || imageUrl || 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&auto=format&fit=crop&q=80';
    const heroImgAlt = heroMediaAttachment?.media.altText || block.payload.imageAltText || title;

    return `
      <div class="hero-container">
        ${eyebrow ? `<span class="hero-eyebrow">${eyebrow}</span>` : ''}
        <h1 class="hero-title">${title}</h1>
        ${description ? `<p class="hero-desc">${description}</p>` : ''}
        <div class="hero-ctas">
          ${primaryCta ? `<a href="${primaryCta.url}" class="btn-primary">${primaryCta.label}</a>` : ''}
          ${secondaryCta ? `<a href="${secondaryCta.url}" class="btn-secondary">${secondaryCta.label}</a>` : ''}
        </div>
        <figure class="media-container hero-figure" style="margin: 2.5rem 0; border-radius: 16px; overflow: hidden; border: 1px solid var(--border-accent, rgba(56,189,248,0.3)); box-shadow: 0 12px 36px rgba(0,0,0,0.5);">
          <img src="${heroImgSrc}" alt="${heroImgAlt}" style="width: 100%; height: auto; display: block; max-height: 480px; object-fit: cover;" class="hero-media-img" loading="eager" />
        </figure>
        ${badgesHtml ? `<div class="trust-strip">${badgesHtml}</div>` : ''}
      </div>
    `;
  }

  private static renderComparison(block: CmsComparisonMatrixBlock): string {
    const { sectionHeading, sectionSubheading, legacyTitle, legacyPoints, modernTitle, modernPoints, imageUrl } =
      block.payload;

    const imgHtml = imageUrl
      ? `
      <div style="margin-bottom: 2rem; border-radius: 12px; overflow: hidden; border: 1px solid var(--border-subtle); max-height: 280px;">
        <img src="${imageUrl}" alt="Modern Threat Intelligence Infrastructure" style="width: 100%; height: 280px; object-fit: cover; display: block;" loading="lazy" />
      </div>`
      : '';

    return `
      <div class="sec-header">
        <h2>${sectionHeading}</h2>
        ${sectionSubheading ? `<p>${sectionSubheading}</p>` : ''}
      </div>
      ${imgHtml}
      <div class="comparison-grid">
        <div class="comp-card legacy">
          <div class="comp-header">
            <span>${legacyTitle}</span>
            <span class="icon-fail">✕ Reactive</span>
          </div>
          <ul class="comp-list">
            ${legacyPoints.map((p) => `<li class="comp-item"><span class="icon-fail">✕</span><span>${p}</span></li>`).join('')}
          </ul>
        </div>
        <div class="comp-card modern">
          <div class="comp-header">
            <span>${modernTitle}</span>
            <span class="icon-pass">✓ Proactive SOC</span>
          </div>
          <ul class="comp-list">
            ${modernPoints.map((p) => `<li class="comp-item"><span class="icon-pass">✓</span><span>${p}</span></li>`).join('')}
          </ul>
        </div>
      </div>
    `;
  }

  private static renderServices(block: CmsServiceCatalogBlock): string {
    const { sectionHeading, sectionSubheading, services } = block.payload;

    return `
      <div class="sec-header">
        <h2>${sectionHeading || 'Five Strategic Service Pillars'}</h2>
        ${sectionSubheading ? `<p>${sectionSubheading}</p>` : ''}
      </div>
      <div class="services-grid">
        ${services
          .map(
            (s) => `
          <div id="${s.serviceId}" class="service-card">
            ${s.imageUrl ? `
            <div class="service-media" style="width: 100%; height: 160px; border-radius: 8px; overflow: hidden; margin-bottom: 1.25rem; border: 1px solid var(--border-subtle); background: rgba(0,0,0,0.3);">
              <img src="${s.imageUrl}" alt="${s.imageAltText || s.serviceTitle}" style="width: 100%; height: 100%; object-fit: cover; display: block;" loading="lazy" />
            </div>` : ''}
            <div class="service-target">${s.targetAudience}</div>
            <h3 class="service-title">${s.serviceTitle}</h3>
            <p class="service-desc">${s.shortDescription}</p>
            <ul class="service-features">
              ${s.featureList.map((f) => `<li>${f}</li>`).join('')}
            </ul>
          </div>
        `
          )
          .join('')}
      </div>
    `;
  }

  private static renderTechEngine(block: CmsTechEngineBlock): string {
    const { sectionHeading, coreMessage, technologies } = block.payload;

    return `
      <div class="sec-header">
        <h2>${sectionHeading}</h2>
        <p>${coreMessage}</p>
      </div>
      <div class="tech-grid">
        ${technologies
          .map(
            (t) => `
          <div class="tech-card">
            ${t.imageUrl ? `
            <div class="tech-media" style="width: 100%; height: 140px; border-radius: 8px; overflow: hidden; margin-bottom: 1rem; border: 1px solid var(--border-accent); background: rgba(0,0,0,0.3);">
              <img src="${t.imageUrl}" alt="${t.imageAltText || t.featureTitle}" style="width: 100%; height: 100%; object-fit: cover; display: block;" loading="lazy" />
            </div>` : ''}
            ${t.metricBadge ? `<span class="tech-metric">${t.metricBadge}</span>` : ''}
            <h4 class="tech-title">${t.featureTitle}</h4>
            <p class="tech-desc">${t.description}</p>
            <ul class="tech-bullets">
              ${t.bulletPoints.map((b) => `<li>${b}</li>`).join('')}
            </ul>
          </div>
        `
          )
          .join('')}
      </div>
    `;
  }

  private static renderIndustryMatrix(block: CmsIndustryMatrixBlock): string {
    const { sectionHeading, sectionSubheading, industries } = block.payload;

    return `
      <div class="sec-header">
        <h2>${sectionHeading}</h2>
        ${sectionSubheading ? `<p>${sectionSubheading}</p>` : ''}
      </div>
      <div class="industry-grid">
        ${industries
          .map(
            (ind) => `
          <div class="industry-card">
            <h4 class="industry-name">${ind.industryName}</h4>
            <p class="industry-risk">${ind.riskProfileSummary}</p>
            <div class="industry-services-tags">
              ${ind.applicableServices.map((svc) => `<span class="service-tag">${svc}</span>`).join('')}
            </div>
          </div>
        `
          )
          .join('')}
      </div>
    `;
  }

  private static renderTrustAudit(block: CmsTrustAuditBlock): string {
    const p = block.payload as any;
    const heading = p.sectionHeading || p.headline || 'Trust Is Our Primary Asset.';
    const sub = p.sectionSubheading || p.body || 'When protecting land titles, national infrastructure, or corporate treasury, trust must be structural, auditable, and absolute.';
    const pillars = p.pillars || [];

    return `
      <div class="sec-header">
        <h2>${heading}</h2>
        <p>${sub}</p>
      </div>
      <div class="trust-grid">
        ${pillars
          .map(
            (item: any) => `
          <div class="trust-card">
            <div class="trust-standard">${item.standardBadge || item.verificationStandard || ''}</div>
            <h4 class="trust-title">${item.title || item.pillarTitle || ''}</h4>
            <p class="trust-desc">${item.description}</p>
          </div>
        `
          )
          .join('')}
      </div>
    `;
  }

  private static renderLeadForm(block: CmsLeadCaptureFormBlock): string {
    const p = block.payload as any;
    const heading = p.headline || p.heading || 'Secure Your Operations & Assets';
    const description = p.subheadline || p.description || 'Speak with our Kampala-based security and asset custody specialists for a confidential risk evaluation.';
    const offices = p.officeLocations || (p.officeDetails ? [{
      city: 'Kampala Headquarters & SOC',
      address: p.officeDetails.location,
      contactEmail: p.officeDetails.inquiries,
      phone: '+256 414 000 000',
      operationalHours: p.officeDetails.operationsHours,
    }] : []);
    const submitLabel = p.submitButtonLabel || 'Request Confidential Assessment';

    const labels = p.localizedLabels || {};
    const options = p.areaOfInquiryOptions || [
      'Private Vault Custody & Safe Deposit',
      'Cash-in-Transit (CIT) & Bullion Logistics',
      'AI Surveillance & Smart Security',
      'Industrial & Mining Security',
      'Threat Assessment & Risk Advisory',
    ];

    return `
      <div id="lead-inquiry-form" class="lead-container">
        <div class="lead-info">
          <h2>${heading}</h2>
          <p>${description}</p>
          ${
            offices.length > 0
              ? `
            <div class="office-card">
              <h4>${offices[0].city}</h4>
              <p><strong>Location:</strong> ${offices[0].address}</p>
              <p><strong>Direct Inquiries:</strong> <bdi class="tech-token" dir="ltr">${offices[0].contactEmail || 'inquiries@sovereign-security.ug'}</bdi> | <bdi class="tech-token" dir="ltr">${offices[0].phone || '+256 414 000 000'}</bdi></p>
              <p><strong>Operations:</strong> ${offices[0].operationalHours || '24/7 Security Operations Centre (SOC)'}</p>
            </div>
          `
              : ''
          }
        </div>
        <form class="form-box" onsubmit="event.preventDefault(); alert('Assessment Request Submitted.');">
          <div class="form-group">
            <label>${labels.name || 'Full Name *'}</label>
            <input type="text" required placeholder="e.g. Director Sarah Mukasa" />
          </div>
          <div class="form-group">
            <label>${labels.company || 'Company / Institution'}</label>
            <input type="text" placeholder="e.g. Uganda Mining Consortium" />
          </div>
          <div class="form-group">
            <label>${labels.email || 'Official Email Address *'}</label>
            <input type="email" dir="ltr" class="ltr-isolated" required placeholder="s.mukasa@institution.ug" />
          </div>
          <div class="form-group">
            <label>${labels.phone || 'Phone Number *'}</label>
            <input type="tel" dir="ltr" class="ltr-isolated" required placeholder="+256 700 000 000" />
          </div>
          <div class="form-group">
            <label>${labels.areaOfInquiry || 'Area of Inquiry *'}</label>
            <select required>
              ${options.map((opt: string) => `<option value="${opt}">${opt}</option>`).join('')}
            </select>
          </div>
          <div class="form-group">
            <label>${labels.scopeNotes || 'Operational Scope / Notes'}</label>
            <textarea rows="3" placeholder="Brief overview of asset protection or logistics requirements..."></textarea>
          </div>
          <div class="form-consent">
            <input type="checkbox" required id="consent-check" />
            <label for="consent-check">${labels.consent || 'I consent to confidential communication regarding institutional security under strict Non-Disclosure protocols.'}</label>
          </div>
          <button type="submit" class="btn-submit">${submitLabel}</button>
        </form>
      </div>
    `;
  }

}

